<template>
  <div class="features">
    <h3 class="ds-heading ds-margin-bottom-48">
      {{ title }}
    </h3>
    <div class="features__table">
      <div
        v-for="(row, index) in tableRows"
        :key="`tableRow-${index}`"
        :class="{ 'features__table__row--border': row.col1.text && !!index }"
        class="features__table__row ds-grid"
      >
        <div
          class="ds-grid__col ds-grid__col--12 ds-md-grid__col--3 ds-padding-left-0"
          :class="{ 'ds-heading-xsmall ds-margin-bottom-12 ds-md-margin-bottom-0': row.col1.text }"
        >
          {{ row.col1.text }}
        </div>
        <div
          class="features__table__col-td ds-grid__col ds-padding-left-0 ds-md-padding-left-24"
          :class="[
            { 'features__table__col-td--with-icon': row.col2.icon },
            [row.col3 ? 'ds-md-grid__col--4' : 'ds-grid__col--9']
          ]"
        >
          <DsIconFrame
            v-if="row.col2.icon"
            class="features__table__icon-frame ds-margin-right-12"
            size="24"
            align-x="center"
            align-y="center"
          >
            <DsIcon v-bind="row.col2.icon" />
          </DsIconFrame>
          <span v-if="row.col2.text && !row.col2.link" class="ds-font-weight-bold">
            {{ row.col2.text }}
          </span>
          <DsLink
            v-if="row.col2.link"
            :open-in-new-window="row.col2.link.openInNewWindow"
            :url="row.col2.link.url"
            :aria-label="row.col2.text"
            :content="row.col2.link.contentType"
            :nuxt="row.col2.link.nuxt"
            :icon-right="{ category: 'arrows', name: 'chevron-right-small' }"
            color="black"
          />
        </div>
        <div
          v-if="row.col3"
          class="features__table__col-td ds-grid__col ds-grid__col--6 ds-md-grid__col--5 ds-padding-right-0"
        >
          <DsIcon
            v-if="row.col3.value === 'false'"
            category="interface"
            name="cross-circle-fill"
            class="features__table__icon ds-color-grey ds-margin-right-24"
          />
          <DsIcon
            v-else-if="row.col3.value === 'true'"
            category="interface"
            name="checkmark-circle-fill"
            class="features__table__icon ds-color-green ds-margin-right-24"
          />
          <span v-else-if="row.col3.value" class="ds-margin-right-8">
            {{ row.col3.value }}
          </span>
          <span v-if="row.col3.text" :class="[row.col3.smallText ? 'ds-text-small' : 'text']">
            {{ row.col3.text }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { DsIcon, DsIconFrame, DsLink } from 'awds'
import { mapperGlobalDataChannel } from '../../../core/data-layer/global-data/mappers'

const { labels, localeGlobalContentData } = useGlobalData()
const { locale: localeRef } = useI18n()
const store = useRoutesStore()

const props = defineProps({
  title: { type: String, required: true },
  paymentMethod: { type: Object, required: true }
})

const filterLabels = computed(() => {
  return labels.value
})

const channels = computed(() => {
  const { getUrl } = useRoutes(store.routes, localeRef)
  const globalDataChannels = localeGlobalContentData.value.globalDataChannel
  const reducer = (acc, cur) => {
    cur.channels.forEach(channel => !acc.includes(channel) && acc.push(channel))
    return acc
  }
  // creates an array with unique channel ids from payment method data
  const channelIds = props.paymentMethod.countryData?.reduce(reducer, []) || []
  // get channels data from globalData
  const channelsData = globalDataChannels.filter(channel => channelIds.includes(channel.sys.id))
  // map channels data
  return channelsData.map(channel => mapperGlobalDataChannel(channel, getUrl))
})

const channelIndicators = computed(() => {
  const reducer = (acc, cur) => {
    if (cur.channels.length === 1 && !acc.includes(cur.channels[0])) {
      acc.push(cur.channels[0])
    }
    return acc
  }
  // creates an array with unique channel ids to use for channel indicators
  // a channel indicator added in the array only when a country has 1 channel
  return channels.value.length > 1 ? props.paymentMethod.countryData?.reduce(reducer, []) : []
})

const locations = computed(() => {
  const reducer = (acc, cur) => {
    if (!acc?.find(item => item.region === cur.region)) {
      // region doesn't exist, create region with country & channels
      acc.push({ region: cur.region, countries: [{ country: cur.country, channels: cur.channels }] })
    } else {
      // region exists add country & channels
      const region = acc?.find(item => item.region === cur.region)
      region.countries.push({ country: cur.country, channels: cur.channels })
    }
    return acc
  }
  // creates an array of grouped countries per region
  // e.g.{ region: '1', countries: [{ country: '1', channels: ['1', '2'] }] },
  return props.paymentMethod.countryData?.reduce(reducer, []) || []
})

const tableRows = computed(() => {
  return [
    ...props.paymentMethod.paymentMethodTypes.map((paymentMethodType, index) => ({
      col1: { text: index === 0 ? filterLabels.value.paymentMethodType : '' },
      col2: { text: paymentMethodType.name, icon: paymentMethodType.icon }
    })),
    ...channels.value.map((channel, index) => ({
      col1: { text: index === 0 ? filterLabels.value.channel : '' },
      col2: { text: channel.name, icon: channel.icon, link: channel.link }
    })),
    ...locations.value.map((location, index) => ({
      col1: { text: index === 0 ? filterLabels.value.availableIn : '' },
      col2: { text: getRegionName(location.region) },
      col3: { text: location.countries.map(item => `${getCountryName(item.country)}${getChannelIndicator(item.channels)}`).join(', ') }
    })),
    ...channelIndicators.value.map((item, index) => ({
      col1: { text: '' },
      col2: { text: '' },
      col3: { text: `${'*'.repeat(index + 1)} ${getChannel(item)?.name} ${filterLabels.value.only}`, smallText: true }
    })),
    {
      col1: { text: filterLabels.value.currencies },
      col2: { text: filterLabels.value.settlementCurrency },
      col3: {
        text: props.paymentMethod.settlementCurrencyAll
          ? getAllCurrencies(props.paymentMethod.settlementCurrencyInfo)
          : getCurrencies(props.paymentMethod.settlementCurrencies, props.paymentMethod.settlementCurrencyInfo)
      }
    },
    ...props.paymentMethod.settlementCurrencyInfo
      ? [{
          col1: { text: '' },
          col2: { text: '' },
          col3: {
            text: props.paymentMethod.settlementCurrencyInfo ? `*${props.paymentMethod.settlementCurrencyInfo}` : null,
            smallText: true
          }
        }]
      : [],
    {
      col1: { text: '' },
      col2: { text: filterLabels.value.processingCurrency },
      col3: {
        text: props.paymentMethod.processingCurrencyAll
          ? getAllCurrencies(props.paymentMethod.processingCurrencyInfo)
          : getCurrencies(props.paymentMethod.processingCurrencies, props.paymentMethod.processingCurrencyInfo)
      }
    },
    ...props.paymentMethod.processingCurrencyInfo
      ? [{
          col1: { text: '' },
          col2: { text: '' },
          col3: {
            text: props.paymentMethod.processingCurrencyInfo ? `*${props.paymentMethod.processingCurrencyInfo}` : null,
            smallText: true
          }
        }]
      : [],
    ...props.paymentMethod.features.filter(item => !!item.value).map((item, index) => ({
      col1: { text: index === 0 ? filterLabels.value.other : '' },
      col2: { text: filterLabels.value[item.name] || '' },
      col3: {
        value: item.value,
        text: item.text
      }
    }))
  ]
})

const getChannel = (id) => {
  const globalDataChannels = localeGlobalContentData.value.globalDataChannel
  return globalDataChannels?.find(channel => channel.sys.id === id) || {}
}

const getRegionName = (id) => {
  const globalDataRegions = localeGlobalContentData.value.globalDataRegion
  const item = globalDataRegions?.find(region => region.sys.id === id)
  return item ? item.name : ''
}

const getCountryName = (id) => {
  const globalDataCountries = localeGlobalContentData.value.globalDataCountry
  const item = globalDataCountries.find(country => country.sys.id === id)
  return item ? item.name : ''
}

const getChannelIndicator = (itemChannels) => {
  // creates a string "*" multiplied by the channel index in the channelIndicators array
  if (channelIndicators.value.length > 0 && itemChannels.length === 1) {
    return channelIndicators.value.reduce((acc, cur, index) => {
      if (itemChannels[0] === cur) {
        acc = '*'.repeat(index + 1)
      }
      return acc
    }, '')
  }
  return ''
}

const getCurrencies = (currencies, currencyInfo) => {
  const currencyList = currencies.map(i => i.isoCode).join(', ')
  return currencyInfo ? `${currencyList}*` : currencyList
}

const getAllCurrencies = (currencyInfo) => {
  return currencyInfo ? `${filterLabels.value.allSupportedCurrencies}*` : filterLabels.value.allSupportedCurrencies
}

</script>

<script>
export default {
  name: 'PaymentMethodFeatures'
}
</script>

<style lang="scss" scoped>
@import 'awds/foundation-vars.scss';
@import 'awds/foundation-mixins.scss';

.features {
  &__table {
    border: 0;
    text-align: left;
    vertical-align: top;
    width: 100%;

    &__icon-frame {
      flex-shrink: 0;
    }

    &__icon {
      flex-shrink: 0;
      transform: translateY(7px); // vertical align baseline with th text
    }

    &__col-td {
      display: flex;
    }

    &__col-td--with-icon {
      transform: translateY(3px); // vertical align baseline with th text
    }

    &__row {
      align-items: baseline;
      margin: $spacing-0 $spacing-0 $spacing-12 $spacing-0;

      &--border {
        border-top: $border-width-1 $border-style $color-grey-500;
        margin-top: $spacing-24;
        padding-top: $spacing-24;
      }
    }
  }
}
</style>
